import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, type LucideIcon } from 'lucide-react';
import { PAGE_SIZE_OPTIONS } from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/lib/i18n';
import type { ListState } from '@/hooks/use-list-state';

export function DataTablePagination({
  listState,
  total,
  loaded,
}: {
  listState: ListState;
  total: number;
  loaded: number;
}) {
  const { t, number } = useI18n();
  const totalPages = Math.max(1, Math.ceil(total / listState.perPage));
  const firstRow = total === 0 ? 0 : (listState.page - 1) * listState.perPage + 1;
  const lastRow = Math.min(listState.page * listState.perPage, total);

  // A single page of results needs no controls at all — but the count is still useful.
  if (total === 0 && loaded === 0) return null;

  return (
    <div className="mt-4 flex flex-col-reverse items-center justify-between gap-3 sm:flex-row">
      <p className="text-muted-soft text-[13px]">
        <span className="text-foreground tabular font-medium">{number(firstRow)}</span>–
        <span className="text-foreground tabular font-medium">{number(lastRow)}</span> {t('common.of')}{' '}
        <span className="text-foreground tabular font-medium">{number(total)}</span> {t('common.rows')}
      </p>

      <div className="flex items-center gap-5">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-muted-soft text-[13px]">{t('common.rowsPerPage')}</span>
          <Select
            value={String(listState.perPage)}
            onValueChange={(value) => listState.setPerPage(Number(value))}
          >
            <SelectTrigger size="sm" className="w-[4.25rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/*
          One hairline group rather than four outlined buttons. Pagination is chrome
          the eye should skip on the way to the data; four bordered boxes in a row read
          as a toolbar and pull far more attention than "next page" deserves.
        */}
        <div className="border-border/80 bg-card flex items-center rounded-lg border p-0.5 shadow-xs">
          <PageButton
            onClick={() => listState.setPage(1)}
            disabled={listState.page <= 1}
            label="First page"
            icon={ChevronsLeft}
          />
          <PageButton
            onClick={() => listState.setPage(listState.page - 1)}
            disabled={listState.page <= 1}
            label="Previous page"
            icon={ChevronLeft}
          />

          <span className="text-muted-foreground tabular px-2.5 text-[13px] whitespace-nowrap">
            <span className="text-foreground font-medium">{listState.page}</span>
            <span className="text-muted-soft"> / {totalPages}</span>
          </span>

          <PageButton
            onClick={() => listState.setPage(listState.page + 1)}
            disabled={listState.page >= totalPages}
            label="Next page"
            icon={ChevronRight}
          />
          <PageButton
            onClick={() => listState.setPage(totalPages)}
            disabled={listState.page >= totalPages}
            label="Last page"
            icon={ChevronsRight}
          />
        </div>
      </div>
    </div>
  );
}

function PageButton({
  onClick,
  disabled,
  label,
  icon: Icon,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  icon: LucideIcon;
}) {
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className="size-7 rounded-md"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
    >
      <Icon className="size-4" />
    </Button>
  );
}
